import { useMutation } from "@tanstack/react-query";
import { BlossomUploader } from '@nostrify/nostrify/uploaders';
import { N64 } from '@nostrify/nostrify/utils';

import type { NostrSigner } from '@nostrify/nostrify';

import { useCurrentUser } from "./useCurrentUser";

const BLOSSOM_SERVERS = [
  'https://blossom.ditto.pub/',
  'https://blossom.dreamith.to/',
  'https://blossom.primal.net/',
];

/** Max dimension (width or height) for uploaded images. */
const MAX_IMAGE_DIMENSION = 2048;
/** JPEG quality for resized images (0-1). */
const JPEG_QUALITY = 0.85;

/**
 * Resize an image file if it exceeds MAX_IMAGE_DIMENSION on either axis.
 * Returns the original file if it's small enough or not a resizable image type.
 * Converts to JPEG for maximum compatibility and smaller file size.
 */
async function resizeImageIfNeeded(file: File): Promise<File> {
  // Only resize raster image types
  if (!file.type.startsWith('image/') || file.type === 'image/gif') {
    return file;
  }

  return new Promise<File>((resolve) => {
    const img = new Image();
    const url = URL.createObjectURL(file);

    img.onload = () => {
      URL.revokeObjectURL(url);

      const { naturalWidth: w, naturalHeight: h } = img;

      // No resize needed — return original
      if (w <= MAX_IMAGE_DIMENSION && h <= MAX_IMAGE_DIMENSION) {
        resolve(file);
        return;
      }

      // Calculate scaled dimensions maintaining aspect ratio
      const scale = Math.min(MAX_IMAGE_DIMENSION / w, MAX_IMAGE_DIMENSION / h);
      const newW = Math.round(w * scale);
      const newH = Math.round(h * scale);

      const canvas = document.createElement('canvas');
      canvas.width = newW;
      canvas.height = newH;

      const ctx = canvas.getContext('2d');
      if (!ctx) {
        resolve(file); // Fallback to original
        return;
      }

      ctx.drawImage(img, 0, 0, newW, newH);

      canvas.toBlob(
        (blob) => {
          if (!blob) {
            resolve(file);
            return;
          }
          // Preserve original filename but change extension to .jpg
          const name = file.name.replace(/\.[^.]+$/, '.jpg') || 'photo.jpg';
          resolve(new File([blob], name, { type: 'image/jpeg' }));
        },
        'image/jpeg',
        JPEG_QUALITY,
      );
    };

    img.onerror = () => {
      URL.revokeObjectURL(url);
      resolve(file); // Fallback to original on error
    };

    img.src = url;
  });
}

export function useUploadFile() {
  const { user } = useCurrentUser();

  return useMutation({
    mutationFn: async (file: File) => {
      if (!user) {
        throw new Error('Must be logged in to upload files');
      }

      // Resize large images before uploading — phone photos are often
      // 10-20MB which exceeds Blossom server limits.
      const resizedFile = await resizeImageIfNeeded(file);

      const uploader = new BlossomUploader({
        servers: BLOSSOM_SERVERS,
        signer: user.signer,
        // Custom fetch with a 90-second per-server timeout.
        // Without this, a hanging server blocks that promise indefinitely.
        // Promise.any() still resolves as soon as any server succeeds.
        fetch: (input, init) => globalThis.fetch(input, {
          ...init,
          signal: AbortSignal.any([
            init?.signal ?? AbortSignal.timeout(90_000),
            AbortSignal.timeout(90_000),
          ]),
        }),
      });

      const tags = await uploader.upload(resizedFile);

      // If the returned URL is missing a file extension, append one from the
      // file name. Blossom URLs are content-addressed (/<sha256>) and
      // may omit the extension. Adding it helps clients infer the media type.
      const ext = getFileExtension(resizedFile.name);
      if (ext) {
        tags[0][1] = appendExtensionIfMissing(tags[0][1], ext);
      }

      const url = tags[0][1];

      // Mirror to all other servers in the background (fire-and-forget).
      // BlossomUploader uses Promise.any(), so only one server has the blob.
      // We mirror to the rest for redundancy (BUD-04).
      const uploadedServer = BLOSSOM_SERVERS.find((s) => url.startsWith(s));
      const mirrorServers = BLOSSOM_SERVERS.filter((s) => s !== uploadedServer);

      if (mirrorServers.length > 0) {
        mirrorToServers(url, mirrorServers, user.signer).catch(() => {
          // Mirroring is best-effort — don't fail the upload if it fails.
        });
      }

      return tags;
    },
  });
}

/** Extract the file extension (with leading dot) from a filename, or empty string if none. */
function getFileExtension(filename: string): string {
  const dotIndex = filename.lastIndexOf('.');
  if (dotIndex <= 0) return '';
  return filename.slice(dotIndex).toLowerCase();
}

/** Append a file extension to a URL if its path doesn't already have one. */
function appendExtensionIfMissing(urlString: string, ext: string): string {
  const url = new URL(urlString);
  const lastSegment = url.pathname.split('/').pop() ?? '';
  if (lastSegment.includes('.')) return urlString;
  url.pathname = url.pathname + ext;
  return url.toString();
}

/** Mirror a blob to additional Blossom servers (BUD-04). */
async function mirrorToServers(
  sourceUrl: string,
  servers: string[],
  signer: NostrSigner,
): Promise<void> {
  const now = Date.now();

  const event = await signer.signEvent({
    kind: 24242,
    content: 'Mirror blob',
    created_at: Math.floor(now / 1000),
    tags: [
      ['t', 'mirror'],
      ['expiration', Math.floor((now + 60_000) / 1000).toString()],
    ],
  });

  const authorization = `Nostr ${N64.encodeEvent(event)}`;

  await Promise.allSettled(
    servers.map((server) =>
      fetch(new URL('/mirror', server), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': authorization,
        },
        body: JSON.stringify({ url: sourceUrl }),
      }),
    ),
  );
}
